import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getUpsellsSelector } from '@modules/shop/core/selectors';
import { IUpsellProduct } from '@modules/shop/core/types';
import { getDefaultSubscriptionSelector } from '@modules/subscriptions/core/selectors';

const DESIGN_MODE = false;

const STORAGE_KEY = 'shop.promo.lastShown';
const ACTIVE_TIME_MS = 45 * 1000; // 45 seconds of active time
const FREQUENCY_MS = 24 * 60 * 60 * 1000; // once per 24h
const TICK_MS = 1000;

// How long the promo must remain visible before we count it as "shown" and
// burn the day's frequency budget. Stops a stray instant-dismiss from costing
// the user their daily exposure.
const DWELL_THRESHOLD_MS = 2000;

const EXCLUDED_PATHS = ['/mitbestellen'];

// Selector used to detect any other modal already open on the page. The shared
// Modal component renders via Portal with a `.modal` className; if one exists
// we postpone the promo so we don't stack on top.
const OTHER_MODAL_SELECTOR = '.modal';

const readLastShown = (): number => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
};

const writeLastShown = (ts: number) => {
  try {
    localStorage.setItem(STORAGE_KEY, String(ts));
  } catch {
    // ignore (private mode, full storage)
  }
};

const pickRandomAvailable = (products: IUpsellProduct[]): IUpsellProduct | null => {
  const candidates = products.filter((p) => p.variants?.some((v) => v.availableForSale));
  if (candidates.length === 0) return null;
  const idx = Math.floor(Math.random() * candidates.length);
  return candidates[idx] ?? null;
};

const isAnotherModalOpen = (ownEl: HTMLElement | null): boolean => {
  // We render through the same Portal-backed Modal, so checking for any
  // `.modal` element would always include ourselves once we're open. Filter
  // out the element that contains our own children (passed in via ownEl).
  const modals = document.querySelectorAll(OTHER_MODAL_SELECTOR);
  for (const m of Array.from(modals)) {
    if (ownEl && m.contains(ownEl)) continue;
    return true;
  }
  return false;
};

const isActiveSubscription = (sub: ISubscription | undefined | null): boolean => {
  return Boolean(sub) && sub?.status === 'ACTIVE';
};

export const useShopPromo = () => {
  const products = useSelector(getUpsellsSelector);
  const defaultSubscription = useSelector(getDefaultSubscriptionSelector);
  const { pathname } = useLocation();

  const [open, setOpen] = useState(false);
  const [product, setProduct] = useState<IUpsellProduct | null>(null);

  // Active time accumulator in milliseconds.
  const elapsedRef = useRef<number>(0);
  // Has the popup already been shown for this tab session?
  const shownThisSession = useRef<boolean>(false);
  // Per-tab lock so the timer doesn't race the localStorage write.
  const isOpenRef = useRef<boolean>(false);
  // Timer that turns "opened" into "counted as shown" after a dwell period.
  const dwellTimerRef = useRef<number | null>(null);

  const isExcludedPath = useMemo(() => EXCLUDED_PATHS.includes(pathname), [pathname]);

  const isEligibleSubscriber = isActiveSubscription(defaultSubscription);

  useEffect(() => {
    if (shownThisSession.current) return;
    if (isExcludedPath) return;
    if (!products || products.length === 0) return;
    // Active subscribers only — paused / cancelled / no subscription don't
    // get the upsell prompt.
    if (!isEligibleSubscriber) return;

    // Design mode: open immediately so we can iterate on visuals.
    if (DESIGN_MODE) {
      const picked = pickRandomAvailable(products);
      if (!picked) return;
      setProduct(picked);
      setOpen(true);
      isOpenRef.current = true;
      shownThisSession.current = true;
      return;
    }

    // Frequency cap: skip the timer entirely if the user has seen one recently.
    const lastShown = readLastShown();
    if (Date.now() - lastShown < FREQUENCY_MS) {
      shownThisSession.current = true;
      return;
    }

    let visible = !document.hidden;

    const onVisibilityChange = () => {
      visible = !document.hidden;
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    const id = window.setInterval(() => {
      if (!visible) return;
      if (isOpenRef.current) return;
      // Don't stack on top of any other open modal (cancellation flow,
      // update-subscription, etc.). We'll try again on the next tick.
      if (isAnotherModalOpen(null)) return;

      elapsedRef.current += TICK_MS;
      if (elapsedRef.current >= ACTIVE_TIME_MS) {
        const picked = pickRandomAvailable(products);
        if (!picked) {
          // No products available right now; reset the clock so we try again
          // once the data updates instead of spinning forever.
          elapsedRef.current = 0;
          return;
        }
        setProduct(picked);
        setOpen(true);
        isOpenRef.current = true;
        shownThisSession.current = true;

        // Mark "shown" only after the dwell window — if the user dismisses
        // within 2s we won't burn the day's budget.
        dwellTimerRef.current = window.setTimeout(() => {
          writeLastShown(Date.now());
          dwellTimerRef.current = null;
        }, DWELL_THRESHOLD_MS);

        window.clearInterval(id);
      }
    }, TICK_MS);

    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [products, isExcludedPath, isEligibleSubscriber]);

  const close = () => {
    setOpen(false);
    isOpenRef.current = false;
    // If the modal closed before the dwell period elapsed, cancel the
    // localStorage write so the user can still see the promo today.
    if (dwellTimerRef.current != null) {
      window.clearTimeout(dwellTimerRef.current);
      dwellTimerRef.current = null;
    }
  };

  return { open, product, close };
};
